import { Grid, TextField } from "@mui/material";
import { useState } from "react";
import * as React from "react";
import { Form, Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { LightTooltip } from "../tooltip/ToolTip";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import StringValidation from "../reusables/yupvalidation/StringValidation";

const AddTenantAdmin = (props) => {
  const { closeDialog, editAdmin, selectedAdmin } = props;

  var userInfo = null;
  var userId = "";
  var firstName = "";
  var lastName = "";
  var email = "";
  var phone = "";
  var nationalId = "";

  var defaultRole = null;
  var roleName = "";
  var defaultTenant = null;
  var tenantId = "";
  var isUserLocked = false;

  if (editAdmin) {
    userInfo = JSON.parse(selectedAdmin);
    userId = userInfo.id;
    firstName = userInfo.firstName;
    lastName = userInfo.lastName;
    email = userInfo.email;
    phone = userInfo.phone;
    nationalId = userInfo.nationalId;

    defaultRole = userInfo.roles[0];
    roleName = userInfo.roles[0].name;
    defaultTenant = userInfo.workerTenant;
    tenantId = userInfo.managerTenant.id;

    isUserLocked = userInfo.locked;
  }

  const axiosPrivate = useAxiosPrivate();

  const resetForm = (props) => {
    // clear input fields
    var now = new Date();
    props.resetForm();
    setSelectedRole("");
    setSelectedTenant("");
    setSelectedRoleKey(now);
    setSelectedTenantKey(now + "a");
  };

  const closeForm = (message) => {
    closeDialog(message);
  };

  // Roles
  const [rolesOpen, setRolesOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleLoading, setRoleLoading] = useState(
    rolesOpen && roles.length === 0
  );
  const [selectedRole, setSelectedRole] = useState(editAdmin ? roleName : "");
  const [selectedRoleKey, setSelectedRoleKey] = useState("roleKey");
  const [roleEmpty, setRoleEmpty] = useState(false);
  const ROLES_URL = "api/v1/roles";
  const handleRoleChange = (event, value) => {
    setSelectedRole(value.name);
    setRoleEmpty(false);
  };

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!roleLoading) {
      return undefined;
    }

    const getRoles = async () => {
      try {
        const response = await axiosPrivate.get(ROLES_URL, {
          signal: controller.signal,
        });
        isMounted && setRoles(response.data.data);
        setRoleLoading(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setRoleLoading(false);
      }
    };

    getRoles();

    return () => {
      isMounted = false;
      controller.abort();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [roleLoading]);

  React.useEffect(() => {
    if (!rolesOpen) {
      setRoles([]);
    }
  }, [rolesOpen]);

  // Tenants
  const [tenantsOpen, setTenantsOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(
    tenantsOpen && tenants.length === 0
  );
  const [selectedTenant, setSelectedTenant] = useState(
    editAdmin ? tenantId : ""
  );
  const [selectedTenantKey, setSelectedTenantKey] = useState("tenantKey");
  const [tenantEmpty, setTenantEmpty] = useState(false);
  const TENANTS_URL = "api/v1/tenants";
  const handleTenantChange = (event, value) => {
    setSelectedTenant(value.id);
    setTenantEmpty(false);
  };

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!tenantLoading) {
      return undefined;
    }

    const getTenants = async () => {
      try {
        const response = await axiosPrivate.get(TENANTS_URL, {
          signal: controller.signal,
        });
        isMounted && setTenants(response.data.data);
        setTenantLoading(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setTenantLoading(false);
      }
    };

    getTenants();

    return () => {
      isMounted = false;
      controller.abort();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [tenantLoading]);

  React.useEffect(() => {
    if (!tenantsOpen) {
      setTenants([]);
    }
  }, [tenantsOpen]);

  // Lock
  const sentenceCaseString = (bool) => {
    const str = bool.toString();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  const [isLocked, setIsLocked] = useState(editAdmin ? isUserLocked : false);
  const [lockedOptionsEmpty, setLockedOptionEmpty] = useState(false);
  const handleIsLockedOptionChange = (event, value) => {
    const lockedValue =
      value === null
        ? false
        : value.label === lockedOptions[0].label
        ? true
        : false;
    setIsLocked(lockedValue);
    setLockedOptionEmpty(false);
  };

  // Validate form
  const validationSchema = Yup.object().shape({
    firstName: StringValidation(2, 40),
    lastName: StringValidation(2, 40),
    phone: Yup.string()
      .required("Required")
      .matches(/^[0-9]\d{9}$/, {
        message: "Please enter valid phone number",
        excludeEmptyString: false,
      }),
    email: Yup.string().email("Please enter valid email").required("Required"),
    /* .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, {
        message: "Please enter valid email",
        excludeEmptyString: false,
      }) */
    nationalId: Yup.string()
      .required("Required")
      .matches(/^[0-9]\d{7}$/, {
        message: "Please enter valid national ID number",
        excludeEmptyString: false,
      }),
  });

  // Common
  const initialValues = editAdmin
    ? {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone,
        nationalId: nationalId,
        roleName: roleName,
        tenantId: tenantId,
        locked: isLocked,
        enabled: !isLocked,
      }
    : {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        nationalId: "",
        roleName: "",
        tenantId: "",
      };

  function onSubmit(values, props) {
    // No role picked
    if (!editAdmin && selectedRole === "") {
      setRoleEmpty(true);
      props.setSubmitting(false);
      return;
    }

    // No tenant picked
    if (!editAdmin && selectedTenant === "") {
      setTenantEmpty(true);
      props.setSubmitting(false);
      return;
    }

    // Locked
    if (editAdmin && lockedOptionsEmpty) {
      // Please select whether suspended
      setLockedOptionEmpty(true);
      props.setSubmitting(false);
      return;
    }

    props.setSubmitting(true);
    createUser(values, props);
  }

  const USER_URL = "/api/v1/users/";
  const createUser = async (values, props) => {
    // Common
    const data = editAdmin
      ? {
          firstName: values.firstName,
          lastName: values.lastName,
          locked: isLocked,
          enabled: !isLocked,
        }
      : {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          nationalId: values.nationalId,
          roleName: selectedRole,
          tenantId: selectedTenant,
        };

    try {
      const response = editAdmin
        ? await axiosPrivate.put(USER_URL + userId, data, {
            headers: {
              "Content-Type": "application/json",
            },
          })
        : await axiosPrivate.post(USER_URL + "save", data, {
            headers: {
              "Content-Type": "application/json",
            },
          });

      //console.log(response.data.message);
      props.setSubmitting(false);
      resetForm(props);
      closeForm(response.data.message);
    } catch (error) {
      props.setSubmitting(false);
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      const errorTitle = editAdmin ? "Update" : "Create";
      setConfirmDialog({
        isOpen: true,
        title: `Failed To ${errorTitle} Tenant`,
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: { onSubmit },
      });
    }
  };

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  return (
    <Grid>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props) => (
          <Form>
            <Stack spacing={2} mt={3} direction="column">
              <Field
                as={TextField}
                label="First name"
                name="firstName"
                placeholder="Enter first name"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="firstName">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Field
                as={TextField}
                label="Last name"
                name="lastName"
                placeholder="Enter last name"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="lastName">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Field
                as={TextField}
                label="Phone"
                name="phone"
                placeholder="Enter phone number"
                disabled={editAdmin}
                fullWidth
                required
                helperText={
                  <ErrorMessage name="phone">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Field
                as={TextField}
                label="Email"
                name="email"
                placeholder="Enter email address"
                disabled={editAdmin}
                fullWidth
                required
                helperText={
                  <ErrorMessage name="email">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Field
                as={TextField}
                label="National ID"
                name="nationalId"
                placeholder="Enter national ID"
                fullWidth
                disabled={editAdmin}
                required
                helperText={
                  <ErrorMessage name="nationalId">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />

              <Autocomplete
                id="role"
                fullWidth
                defaultValue={defaultRole}
                onChange={handleRoleChange}
                disabled={editAdmin}
                key={selectedRoleKey}
                open={rolesOpen}
                onOpen={() => {
                  setRoleLoading(true);
                  setRolesOpen(true);
                }}
                onClose={() => {
                  setRoleLoading(false);
                  setRolesOpen(false);
                }}
                isOptionEqualToValue={(role, value) => role.name === value.name}
                getOptionLabel={(role) => (role.name ? role.name : "")}
                getOptionDisabled={(role) => role.name !== "TENANT_MANAGER"}
                options={roles}
                loading={roleLoading}
                loadingText="Loading roles..."
                noOptionsText="No roles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Role"
                    name="role"
                    error={roleEmpty}
                    helperText={roleEmpty ? "Role required" : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {roleLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                id="tenant"
                fullWidth
                defaultValue={defaultTenant}
                onChange={handleTenantChange}
                disabled={editAdmin}
                key={selectedTenantKey}
                open={tenantsOpen}
                onOpen={() => {
                  setTenantLoading(true);
                  setTenantsOpen(true);
                }}
                onClose={() => {
                  setTenantLoading(false);
                  setTenantsOpen(false);
                }}
                isOptionEqualToValue={(tenant, value) =>
                  tenant.name === value.name
                }
                getOptionLabel={(tenant) => (tenant.name ? tenant.name : "")}
                options={tenants}
                loading={tenantLoading}
                loadingText="Loading tenants..."
                noOptionsText="No tenants"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tenant"
                    name="tenant"
                    error={tenantEmpty}
                    helperText={tenantEmpty ? "Tenant required" : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {tenantLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />

              {editAdmin && (
                <Autocomplete
                  disablePortal
                  id="locked"
                  defaultValue={sentenceCaseString(isLocked)}
                  onChange={handleIsLockedOptionChange}
                  fullWidth
                  options={lockedOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Is locked"
                      placeholder="Is locked"
                      error={lockedOptionsEmpty}
                      helperText={
                        lockedOptionsEmpty ? "Is locked required" : ""
                      }
                    />
                  )}
                />
              )}
            </Stack>

            <Stack spacing={2} mt={3} direction="row" justifyContent="flex-end">
              <LightTooltip title="Clear form">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 8,
                    width: 120,
                    backgroundColor: "#808080",
                    padding: "7px 14px",
                    fontSize: "14px",
                    color: "white",
                    ":hover": {
                      bgcolor: "#696969",
                      color: "white",
                    },
                  }}
                  onClick={() => {
                    resetForm(props);
                  }}
                >
                  Reset
                </Button>
              </LightTooltip>

              <LightTooltip title="Submit details">
                <Button
                  loading={props.isSubmitting}
                  loadingPosition="start"
                  disabled={props.isSubmitting}
                  type="submit"
                  sx={{
                    width: 120,
                  }}
                  variant="solid"
                >
                  {props.isSubmitting ? "Submitting" : "Submit"}
                </Button>
              </LightTooltip>
            </Stack>
          </Form>
        )}
      </Formik>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Grid>
  );
};

export default AddTenantAdmin;

const lockedOptions = [{ label: "True" }, { label: "False" }];
